import { useEffect, useState } from 'react'

import { FileText, Pencil, Plus, TrashBin, Wrench, Xmark } from '@gravity-ui/icons'
import { Button, EmptyState, InputGroup, Table, Tooltip } from '@heroui/react'
import { useTranslation } from 'react-i18next'

import { useDebounce } from '@/hooks/useDebounce'
import { useWarehouseStore } from '@/store/warehouse.store'

interface WarehouseHistoryTableProps {
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
}

export function WarehouseHistoryTable({ onEdit, onDelete }: WarehouseHistoryTableProps) {
  const { t } = useTranslation()
  const { history, historyFilters, setHistoryFilters } = useWarehouseStore()

  const [searchValue, setSearchValue] = useState(historyFilters.name)
  const debouncedSearchValue = useDebounce(searchValue, 400)

  // Sync debounced search to store filter
  useEffect(() => {
    if (debouncedSearchValue !== historyFilters.name) {
      setHistoryFilters({ name: debouncedSearchValue })
    }
  }, [debouncedSearchValue, historyFilters.name, setHistoryFilters])

  // Reset local search value if store filters are reset
  useEffect(() => {
    setSearchValue(historyFilters.name)
  }, [historyFilters.name])

  return (
    <div className="space-y-4">
      <div>
        <InputGroup className="w-80 max-w-xs" variant="secondary">
          <InputGroup.Input
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
            placeholder={t('warehouse.search_placeholder')}
          />
          {searchValue && (
            <InputGroup.Suffix>
              <Button
                isIconOnly
                aria-label="Clear"
                size="sm"
                variant="ghost"
                onClick={() => setSearchValue('')}
              >
                <Xmark className="size-3" />
              </Button>
            </InputGroup.Suffix>
          )}
        </InputGroup>
      </div>

      <Table className="min-h-[200px]">
        <Table.ScrollContainer className="max-h-[calc(100vh-theme(spacing.72))]">
          <Table.Content aria-label={t('warehouse.history_table_label')}>
            <Table.Header className="sticky top-0 z-10">
              <Table.Column id="type" className="w-12" />
              <Table.Column id="name">{t('warehouse.article_name')}</Table.Column>
              <Table.Column id="vehicle">{t('warehouse.vehicle')}</Table.Column>
              <Table.Column id="count">{t('warehouse.count')}</Table.Column>
              <Table.Column id="date">{t('warehouse.date')}</Table.Column>
              <Table.Column id="total_cost">{t('warehouse.total_cost')}</Table.Column>
              <Table.Column id="actions"></Table.Column>
            </Table.Header>
            <Table.Body
              renderEmptyState={() => (
                <EmptyState className="flex h-30 mt-6 w-full flex-col items-center justify-center gap-4 text-center">
                  <FileText className="size-6 text-zinc-400" />
                  <span className="text-sm text-zinc-500">{t('common.no_results', 'Нет данных для отображения')}</span>
                </EmptyState>
              )}
            >
              {history.map(a => {
                const totalCost = a.count * a.cost
                return (
                  <Table.Row key={a.id} className={`hover:bg-default-50 ${a.isInstall ? 'cursor-default' : 'cursor-pointer'}`}>
                    {/* Operation Type Icon Block */}
                    <Table.Cell onClick={() => !a.isInstall && onEdit(a.id)}>
                      {a.isInstall ? (
                        <Tooltip delay={0}>
                          <Button
                            isIconOnly
                            size="sm"
                            variant="ghost"
                            className="w-8 h-8 min-w-8 rounded-lg flex items-center justify-center shrink-0 bg-amber-50 text-amber-600 dark:bg-amber-950/30 dark:text-amber-400 p-0 border-none cursor-default"
                          >
                            <Wrench className="size-4" />
                          </Button>
                          <Tooltip.Content>
                            <p>{t('warehouse.type_out_badge')}</p>
                          </Tooltip.Content>
                        </Tooltip>
                      ) : (
                        <Tooltip delay={0}>
                          <Button
                            isIconOnly
                            size="sm"
                            variant="ghost"
                            className="w-8 h-8 min-w-8 rounded-lg flex items-center justify-center shrink-0 bg-emerald-50 text-emerald-600 dark:bg-emerald-950/30 dark:text-emerald-400 p-0 border-none cursor-default"
                          >
                            <Plus className="size-4" />
                          </Button>
                          <Tooltip.Content>
                            <p>{t('warehouse.type_in_badge')}</p>
                          </Tooltip.Content>
                        </Tooltip>
                      )}
                    </Table.Cell>

                    {/* Part Name */}
                    <Table.Cell onClick={() => !a.isInstall && onEdit(a.id)}>
                      <div className="flex flex-col">
                        <span className="font-bold text-foreground">{a.parsedName}</span>
                        {a.description && (
                          <span className="text-xs text-zinc-500 line-clamp-1 max-w-md">
                            {a.description}
                          </span>
                        )}
                      </div>
                    </Table.Cell>

                    {/* Target Vehicle plate */}
                    <Table.Cell onClick={() => !a.isInstall && onEdit(a.id)}>
                      {a.isInstall && a.vehiclePlate ? (
                        <span className="font-mono font-bold text-zinc-800 dark:text-zinc-200 bg-zinc-100 dark:bg-zinc-800 px-2 py-0.5 rounded border border-zinc-200 dark:border-zinc-700">
                          {a.vehiclePlate}
                        </span>
                      ) : (
                        <span className="text-zinc-400">—</span>
                      )}
                    </Table.Cell>

                    {/* Count */}
                    <Table.Cell onClick={() => !a.isInstall && onEdit(a.id)}>
                      <span className="font-semibold">{a.count}</span>
                    </Table.Cell>

                    {/* Date */}
                    <Table.Cell onClick={() => !a.isInstall && onEdit(a.id)}>
                      <span className="font-mono text-zinc-600 dark:text-zinc-400">{a.date}</span>
                    </Table.Cell>

                    {/* Total Cost */}
                    <Table.Cell onClick={() => !a.isInstall && onEdit(a.id)}>
                      <span className="font-bold text-zinc-900 dark:text-zinc-100">{totalCost.toFixed(2).replace('.', ',')} BYN</span>
                    </Table.Cell>

                    {/* Actions */}
                    <Table.Cell>
                      <div className="flex justify-end gap-2">
                        {!a.isInstall && (
                          <Tooltip delay={0}>
                            <Button
                              isIconOnly
                              size="sm"
                              variant="tertiary"
                              onClick={e => {
                                e.stopPropagation()
                                onEdit(a.id)
                              }}
                            >
                              <Pencil className="size-4" />
                            </Button>
                            <Tooltip.Content>
                              <p>{t('common.edit')}</p>
                            </Tooltip.Content>
                          </Tooltip>
                        )}

                        <Tooltip delay={0}>
                          <Button
                            isIconOnly
                            size="sm"
                            variant="danger-soft"
                            onClick={e => {
                              e.stopPropagation()
                              onDelete(a.id)
                            }}
                          >
                            <TrashBin className="size-4" />
                          </Button>
                          <Tooltip.Content>
                            <p>{t('common.delete')}</p>
                          </Tooltip.Content>
                        </Tooltip>
                      </div>
                    </Table.Cell>
                  </Table.Row>
                )
              })}
            </Table.Body>
          </Table.Content>
        </Table.ScrollContainer>
      </Table>
    </div>
  )
}
